export interface DepositWalletActionDeposit {
  id: string
  depositGuid?: string
  txid?: string
  amount: string
  timestamp: string
}

export interface DepositWalletActionItem {
  id: string
  timestamp: string
  assetName: string
  network: string
  addressPrefix: string
  addressMiddle: string
  addressSuffix: string
  sourceAddress: string
  deposits: DepositWalletActionDeposit[]
}

export type DepositDisplayStatus = 'completed' | 'pending' | 'declined' | 'information_required'

export type FormatDepositAmountFn = (amount: number, assetName: string) => string

export const getDepositDisplayStatus = (record: { status?: number | string; asset_type?: string }): DepositDisplayStatus => {
  const statusValue = Number(record?.status)
  if (statusValue === 1) return 'completed'
  if (statusValue === -1) return 'declined'
  if (statusValue === 0 && record?.asset_type === 'TOKEN') return 'information_required'
  if (statusValue === 0 || statusValue === 2) return 'pending'
  return 'pending'
}

export const isDepositInformationRequired = (record: { status?: number | string; asset_type?: string }) => {
  return getDepositDisplayStatus(record) === 'information_required'
}

const splitSourceAddress = (sourceAddress: string) => {
  const prefix = sourceAddress.slice(0, 4)
  const suffix = sourceAddress.slice(-4)
  const middle = sourceAddress.length > 8 ? sourceAddress.slice(4, -4) : ''
  return { prefix, middle, suffix }
}

export const buildPendingActionItems = (
  results: Array<any> = [],
  formatAmount: FormatDepositAmountFn,
): DepositWalletActionItem[] => {
  const grouped = new Map<string, any[]>()

  results.forEach((item: any) => {
    const key = `${item.source_address || ''}__${item.network || ''}__${item.asset_name || ''}`
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)!.push(item)
  })

  return Array.from(grouped.values()).map((group: any[]) => {
    const first = group[0]
    const sourceAddress = String(first.source_address || '')
    const { prefix, middle, suffix } = splitSourceAddress(sourceAddress)

    return {
      id: `${first.source_address || ''}-${first.network || ''}-${first.asset_name || ''}`,
      timestamp: first.created,
      assetName: first.asset_name,
      network: first.network,
      addressPrefix: prefix,
      addressMiddle: middle,
      addressSuffix: suffix,
      sourceAddress,
      deposits: group.map((item: any) => ({
        id: item.txid || item.deposit_guid,
        depositGuid: item.deposit_guid,
        txid: item.txid || '',
        amount: `${formatAmount(item.amount, item.asset_name)} ${item.asset_name}`,
        timestamp: item.updated || item.created,
      })),
    }
  })
}

export const findActionItemsForDepositGuid = (items: DepositWalletActionItem[], depositGuid: string) => {
  const guid = String(depositGuid || '')
  if (!guid) return []
  return items.filter((item) =>
    item.deposits.some((deposit) => deposit.depositGuid === guid || deposit.id === guid),
  )
}

export const buildActionItemFromDepositRecord = (
  record: any,
  formatAmount: FormatDepositAmountFn,
): DepositWalletActionItem | null => {
  const sourceAddress = String(
    record?.source_address
      || record?.from_address
      || record?.sender_address
      || record?.white_address
      || record?.address
      || '',
  )
  if (!sourceAddress) return null

  const { prefix, middle, suffix } = splitSourceAddress(sourceAddress)
  const depositGuid = record.deposit_guid || record.guid
  const assetName = record.asset_name || ''

  return {
    id: `${sourceAddress}-${record.network || ''}-${assetName}`,
    timestamp: record.updated || record.created || '',
    assetName,
    network: record.network || assetName,
    addressPrefix: prefix,
    addressMiddle: middle,
    addressSuffix: suffix,
    sourceAddress,
    deposits: [
      {
        id: record.txid || depositGuid,
        depositGuid,
        txid: record.txid || '',
        amount: `${formatAmount(Number(record.amount), assetName)} ${assetName}`,
        timestamp: record.updated || record.created || '',
      },
    ],
  }
}

export const fetchPendingDepositActionItems = async (
  store: { dispatch: (action: string, payload: any) => Promise<{ data: any }> },
  formatAmount: FormatDepositAmountFn,
) => {
  const { data } = await store.dispatch('request', {
    method: 'GET',
    endpoint: 'deposit/white-address/pending',
  })
  const results = Array.isArray(data?.results) ? data.results : []
  return buildPendingActionItems(results, formatAmount)
}

export const formatShortTxid = (txid?: string) => {
  const value = String(txid || '').trim()
  if (!value) return ''
  if (value.length <= 12) return value
  return `${value.slice(0, 4)}...${value.slice(-4)}`
}

export const getTxExplorerUrl = (network = '', txid?: string): string | null => {
  const tx = String(txid || '').trim()
  if (!tx) return null
  const net = String(network || '').toUpperCase()
  if (net === 'ETH' || net === 'ERC20') return `https://etherscan.io/tx/${tx}`
  if (net === 'BTC') return `https://mempool.space/tx/${tx}`
  if (net === 'TRON' || net === 'TRC20') return `https://tronscan.org/#/transaction/${tx}`
  if (net === 'BSC' || net === 'BEP20') return `https://bscscan.com/tx/${tx}`
  if (net === 'POLYGON' || net === 'MATIC') return `https://polygonscan.com/tx/${tx}`
  if (net === 'ARBITRUM') return `https://arbiscan.io/tx/${tx}`
  return null
}

export const submitDepositDetailsMetadata = async (
  store: { dispatch: (action: string, payload: any) => Promise<unknown> },
  item: DepositWalletActionItem,
  metadata: Record<string, any>,
) => {
  const guids = item.deposits
    .map((deposit) => deposit.depositGuid)
    .filter((guid): guid is string => !!guid)

  await store.dispatch('request', {
    method: 'POST',
    endpoint: 'deposit/white-address/metadata',
    data: {
      type: 'DEPOSIT',
      guids,
      metadata: JSON.stringify(metadata || {}),
    },
  })
}

export const depositDetailsActionItemsMock: DepositWalletActionItem[] = [
  {
    id: 'wallet-1',
    timestamp: '2026-03-25 14:10:45 UTC',
    assetName: 'BTC',
    network: 'BTC',
    addressPrefix: '0x71',
    addressMiddle: 'C9fA3b2D4e5F67890123456789ABCDEF',
    addressSuffix: '8912',
    sourceAddress: '0x71CC9fA3b2D4e5F67890123456789ABCDEF8912',
    deposits: [
      { id: 'wallet-1-deposit-1', amount: '0.001 BTC', timestamp: '2026-03-25 14:05:01 UTC' },
      { id: 'wallet-1-deposit-2', amount: '0.02 BTC', timestamp: '2026-03-25 14:05:01 UTC' },
    ],
  },
  {
    id: 'wallet-2',
    timestamp: '2026-03-24 14:10:45 UTC',
    assetName: 'USDT',
    network: 'ETH',
    addressPrefix: '0xab',
    addressMiddle: '1234567890abcdefABCDEF123456789',
    addressSuffix: 'Def0',
    sourceAddress: '0xab1234567890abcdefABCDEF123456789Def0',
    deposits: [
      { id: 'wallet-2-deposit-1', amount: '5000.0000 USDT', timestamp: '2026-03-24 14:10:45 UTC' },
    ],
  },
  {
    id: 'wallet-3',
    timestamp: '2026-03-22 14:10:45 UTC',
    assetName: 'USDT',
    network: 'ETH',
    addressPrefix: '0xab',
    addressMiddle: '1234567890abcCEAABCDEF123456789',
    addressSuffix: 'Aa32',
    sourceAddress: '0xab1234567890abcCEAABCDEF123456789Aa32',
    deposits: [
      { id: 'wallet-3-deposit-1', amount: '5000.0000 USDT', timestamp: '2026-03-22 14:10:45 UTC' },
    ],
  },
  {
    id: 'wallet-4',
    timestamp: '2026-03-21 09:32:18 UTC',
    assetName: 'ETH',
    network: 'ETH',
    addressPrefix: '0x4f',
    addressMiddle: 'AC9B12ef4567890abCDef1234567890',
    addressSuffix: '19B7',
    sourceAddress: '0x4fAC9B12ef4567890abCDef123456789019B7',
    deposits: [
      { id: 'wallet-4-deposit-1', amount: '1.2500 ETH', timestamp: '2026-03-21 09:32:18 UTC' },
      { id: 'wallet-4-deposit-2', amount: '0.5000 ETH', timestamp: '2026-03-21 09:22:14 UTC' },
      { id: 'wallet-4-deposit-3', amount: '0.1000 ETH', timestamp: '2026-03-21 08:58:40 UTC' },
    ],
  },
  {
    id: 'wallet-5',
    timestamp: '2026-03-20 18:06:11 UTC',
    assetName: 'USDT',
    network: 'ETH',
    addressPrefix: '0xbc',
    addressMiddle: '0AbCDeF1234567890abcdef12345678',
    addressSuffix: '7Fe2',
    sourceAddress: '0xbc0AbCDeF1234567890abcdef123456787Fe2',
    deposits: [
      { id: 'wallet-5-deposit-1', amount: '15000.0000 USDT', timestamp: '2026-03-20 18:06:11 UTC' },
    ],
  },
  {
    id: 'wallet-6',
    timestamp: '2026-03-19 07:48:52 UTC',
    assetName: 'BTC',
    network: 'BTC',
    addressPrefix: '0x9a',
    addressMiddle: '9876543210FEDCBAabcdef123456789',
    addressSuffix: '0Cd4',
    sourceAddress: '0x9a9876543210FEDCBAabcdef1234567890Cd4',
    deposits: [
      { id: 'wallet-6-deposit-1', amount: '0.015 BTC', timestamp: '2026-03-19 07:48:52 UTC' },
      { id: 'wallet-6-deposit-2', amount: '0.020 BTC', timestamp: '2026-03-19 07:36:19 UTC' },
    ],
  },
]
